import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type Finding,
  type FindingReply,
  type VendorDocument,
} from '../lib/api'
import type { DialogSave, Save } from '../lib/save'
import { statusWord } from '../lib/status'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from './DataTable'
import { findingFooter } from './footers'
import { DialogFooter, Status, Toggletip, leavesDialog, useInDialog, Picker, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { DateRuleNote } from './DateRuleNote'
import { checkFindingDue, todayIso } from '../lib/dateRules'
import '../styles/sweep.css'
import { severityTone } from './vendor/facts'

// Findings on a record: the vendor's tab, the assessment's panel, and the rows
// the register across every vendor is built from.
//
// WHY THE ROW IS EDITABLE AND NOT A LINK TO A PAGE
//
// A remediation review is one person working through twenty of these in an
// hour. Owner, due date and status are what move in that hour, so all three
// move in the row. A screen that costs a page load each is a screen that gets
// replaced by a spreadsheet.
//
// WHERE THE TRANSITION TABLE COMES FROM
//
// The API is the authority and refuses every move against
// packages/core/domain/findings.go. It also answers a write with allowedNext,
// the set the row may reach from where it now stands, and where that is present
// it is what the row offers.
//
// The list routes do not carry it. GET /v1/findings serves the row alone, so a
// register of forty rows knows forty statuses and no allowed sets, and a screen
// with no buttons until somebody writes to a row is a screen nobody can work.
// MOVES below is therefore a mirror of that domain table, used to draw the
// buttons before the server has spoken about a row and narrowed by allowedNext
// the moment it has. It carries two things allowedNext does not and the buttons
// cannot be drawn without: which moves need words, and which belong to an
// approved exception rather than to a person. When the register route grows an
// allowed set per row this table stops being read on load.

// The four the schema permits, for the override control and the register's
// filter. This is the vocabulary, not the derivation: what a gate earns is
// decided in packages/core/domain and arrives on the finding as
// derivedSeverity.
export const FINDING_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']


const SEVERITY_RANK: Record<string, number> = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 }

// The status label, in the words and tones the remediation register uses, so a
// finding reads the same on the register, in its drawer and on the vendor.
// Resolved and withdrawn are finished and go quiet.
// The words come from the one vocabulary (lib/status.ts): In progress reads
// Remediating and Accepted reads Risk accepted, because "Accepted" also named
// an assessment state, a document decision and a clarification outcome.
const STATUS_WORD: Record<string, { text: string; tone: Tone }> = Object.fromEntries(
  ['OPEN', 'IN_PROGRESS', 'ACCEPTED', 'RESOLVED', 'WITHDRAWN'].map((c) => {
    const w = statusWord('finding', c)
    return [c, { text: w.word, tone: w.tone }]
  }),
)

// Standing first, then the closures, so sorting by status puts the work at the
// top rather than sorting alphabetically into IN_PROGRESS, OPEN, RESOLVED.
const STATUS_RANK: Record<string, number> = {
  OPEN: 1,
  IN_PROGRESS: 2,
  ACCEPTED: 3,
  RESOLVED: 4,
  WITHDRAWN: 5,
}

// Where a finding came from, in the words the register, the drawer and the
// risk register all use. The four sources migration 0049 allows: a rule on the
// model (GATE), a scored answer (ANSWER), the outside-in website check
// (OBSERVED) and a person (MANUAL). Only MANUAL is by hand; labelling every
// non-gate finding that way told a reader a person raised what a check did.
const SOURCE_WORD: Record<string, string> = {
  GATE: 'Rule',
  ANSWER: 'Answer',
  OBSERVED: 'Website check',
  MANUAL: 'By hand',
}

export function findingSourceWord(source: string): string {
  return SOURCE_WORD[source] ?? findingWords(source)
}

function findingWords(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

// Still work somebody owes. The same two statuses the ageing read and the
// standing-finding index use, and the reason the register defaults to them.
export function findingStanding(status: string): boolean {
  return status === 'OPEN' || status === 'IN_PROGRESS'
}

// The row as it now stands, keeping the two columns a write does not answer
// with. The vendor's name and code are a join the list routes add; a write
// answers with the stored finding alone, so replacing a row wholesale with the
// reply blanks the vendor column on the register.
export function mergedFinding(before: Finding, after: Finding): Finding {
  return {
    ...after,
    vendorName: after.vendorName ?? before.vendorName,
    vendorCode: after.vendorCode ?? before.vendorCode,
  }
}

// One permitted move, mirroring packages/core/domain/findings.go. See the file
// header for why the mirror exists and what narrows it.
//
// Why each move exists is written beside it in the domain table and not here.
// The buttons carry the act and nothing else, and a move that needs words asks
// for one Reason, which the API files as the finding's note and puts on the
// audit event.
type Move = {
  to: string
  requiresNote: boolean
  byExceptionOnly: boolean
}

const MOVES: Record<string, Move[]> = {
  OPEN: [
    { to: 'IN_PROGRESS', requiresNote: false, byExceptionOnly: false },
    { to: 'RESOLVED', requiresNote: true, byExceptionOnly: false },
    { to: 'ACCEPTED', requiresNote: false, byExceptionOnly: true },
    { to: 'WITHDRAWN', requiresNote: true, byExceptionOnly: false },
  ],
  IN_PROGRESS: [
    { to: 'RESOLVED', requiresNote: true, byExceptionOnly: false },
    { to: 'OPEN', requiresNote: true, byExceptionOnly: false },
    { to: 'ACCEPTED', requiresNote: false, byExceptionOnly: true },
    { to: 'WITHDRAWN', requiresNote: true, byExceptionOnly: false },
  ],
  RESOLVED: [{ to: 'OPEN', requiresNote: true, byExceptionOnly: false }],
  ACCEPTED: [{ to: 'OPEN', requiresNote: false, byExceptionOnly: false }],
  WITHDRAWN: [],
}

// What this row offers. allowedNext is the API's own answer and wins wherever
// it is present; the mirror above supplies the two facts allowedNext does not
// carry, and the order the buttons appear in.
function movesFor(finding: Finding): Move[] {
  const table = MOVES[finding.status] ?? []
  const allowed = finding.allowedNext
  if (!allowed) return table
  return table.filter((m) => allowed.includes(m.to))
}

// The words on a move button, which depend on where the finding is coming from
// as well as where it is going: putting work down and reopening a closure are
// the same target status and are not the same act.
function moveLabel(from: string, to: string): string {
  if (to === 'IN_PROGRESS') return 'Start work'
  if (to === 'RESOLVED') return 'Resolve'
  if (to === 'WITHDRAWN') return 'Withdraw'
  if (to === 'ACCEPTED') return 'Accept the risk'
  if (to === 'OPEN') return from === 'IN_PROGRESS' ? 'Put back in the queue' : 'Reopen'
  return findingWords(to)
}

// The short outcome shown under the row once a move has been made.
function moveDone(from: string, to: string): string {
  if (to === 'IN_PROGRESS') return 'Work started.'
  if (to === 'RESOLVED') return 'Resolved.'
  if (to === 'WITHDRAWN') return 'Withdrawn.'
  if (to === 'OPEN') return from === 'IN_PROGRESS' ? 'Back in the queue.' : 'Reopened.'
  return 'Now ' + findingWords(to).toLowerCase() + '.'
}

// Where the due date stands, as a label, only while the finding is still owed.
function ageingTone(days: number): Tone {
  if (days > 0) return 'crit'
  if (days === 0) return 'warn'
  return days >= -7 ? 'warn' : 'plain'
}

// Ageing as a sentence rather than a signed integer. The API counts the days;
// this decides only which side of the due date they fall on.
function ageingText(days: number): string {
  if (days > 0) return days === 1 ? '1 day overdue' : days + ' days overdue'
  if (days === 0) return 'due today'
  const left = Math.abs(days)
  return left === 1 ? '1 day left' : left + ' days left'
}

type WriteState = {
  busy: boolean
  error: ApiError | null
  said: string
  run: (work: Promise<FindingReply>, done?: string) => void
}

// Every write answers with the row it wrote and one sentence saying what
// happened. The sentence is shown as it arrives: the API knows which rule it
// just applied and a console composing its own would drift from it.
//
// A status move is the exception. The API's sentence for a move carries the
// reason the move exists, which is the explanatory text the Resolve, Withdraw
// and Put back forms no longer show, so a move passes its own short outcome
// as done and the sentence is not printed.
//
// `save` is one act of the save of the dialog the cell is drawn in
// (lib/save.ts). The cell is drawn afresh each time the dialog opens, so a
// busy of its own would forget a write that is still out and let it be sent
// a second time; the dialog's save is held by the view and remembers. On a
// page there is none and the cell waits for its own answer.
export function useRowWrite(onChanged: (f: Finding) => void, save?: Save): WriteState {
  const [own, setOwn] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [said, setSaid] = useState('')
  const send = save?.send

  const run = useCallback(
    (work: Promise<FindingReply>, done?: string) => {
      setError(null)
      setSaid('')
      const worked = (reply: FindingReply) => {
        setSaid(done ?? reply.note)
        onChanged(reply.finding)
      }
      const failed = (err: unknown) => setError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)))
      if (send) {
        send(work, { done: worked, failed })
        return
      }
      setOwn(true)
      work
        .then(worked)
        .catch(failed)
        .finally(() => setOwn(false))
    },
    [onChanged, send],
  )

  return { busy: save ? save.busy : own, error, said, run }
}

// A refused write inside a table cell. Not ErrorBox: the panel loaded and one
// act in it was refused, and a heading reading "could not be loaded" would send
// the reader to the wrong end of the request.
function WriteOutcome({ state }: { state: WriteState }): JSX.Element | null {
  if (state.error) {
    // The issues and the API's own remedy, and nothing invented from the
    // status code: on a refused move that guess reads as advice about a
    // duplicate identifier, which is not what happened.
    const { code, message, issues, sentRemedy } = state.error
    return (
      <p className="cell-refusal" role="alert">
        <strong>{code}</strong> {message}
        {issues.map((i) => ' ' + i.detail).join('')}
        {sentRemedy ? ' ' + sentRemedy : ''}
      </p>
    )
  }
  if (state.said) {
    return (
      <p className="cell-said" role="status">
        {state.said}
      </p>
    )
  }
  return null
}

// An editor that saves as it is left. On a page, leaving it is done on
// purpose. In a dialog, focus also leaves when the dialog is closed (Escape, a
// press on the scrim, Close, Cancel), and that must not save: there it saves
// only when focus goes on to something else in the same dialog, or on Enter.
// Close and Cancel are in the same dialog and are still ways out of it, which
// the frame marks (kit/frame.ts, LEAVES). What was typed and not saved is
// dropped when the dialog closes, and the line under the box says so.
export function blurSaves(inDialog: boolean, to: unknown, from: unknown): boolean {
  if (!inDialog) return true
  if (to === null || to === undefined || leavesDialog(to)) return false
  const dialogOf = (el: unknown) => (el as { closest?: (s: string) => unknown } | null)?.closest?.('[role="dialog"]') ?? null
  return dialogOf(to) !== null && dialogOf(to) === dialogOf(from)
}

// # WHY THE BOX IS NOT SWITCHED OFF WHILE ITS SAVE IS OUT
//
// It was, and a box that is switched off cannot hold focus. Enter saved and
// dropped the keyboard onto the page, where on a page the browser's own blur
// of the dead box saved a second time. Tab moved on, and the box going dead
// in the same instant was taken by the dialog for focus lost, which sent the
// keyboard to Close. So while its save is out the box is read only: it keeps
// focus, takes no typing, and a second Enter or a blur sends nothing more.
// What a refusal says is under the box, where the words that were typed
// still are.
function boxLine(busy: boolean, inDialog: boolean): string {
  if (busy) return 'Saving'
  return 'Not saved yet, press Enter' + (inDialog ? '. Closing leaves it as it was' : '')
}

export function OwnerCell({ finding, onChanged, save }: { finding: Finding; onChanged: (f: Finding) => void; save?: DialogSave }): JSX.Element {
  const inDialog = useInDialog()
  const [draft, setDraft] = useState(finding.ownerUserId)
  const write = useRowWrite(onChanged, save?.of('owner'))
  const may = can('finding.manage')
  const dirty = draft.trim() !== finding.ownerUserId

  useEffect(() => setDraft(finding.ownerUserId), [finding.id, finding.ownerUserId])

  function commit() {
    if (write.busy) return
    const next = draft.trim()
    if (next === finding.ownerUserId) return
    // An empty box is a slip rather than an instruction. The record has no
    // state for an unowned finding and this is not the screen that invents one.
    if (next === '') {
      setDraft(finding.ownerUserId)
      return
    }
    write.run(api.patchFinding(finding.id, { ownerUserId: next }))
  }

  return (
    <div className="cell-edit">
      <label className="vh" htmlFor={'owner-' + finding.id}>
        Owner of {finding.title}
      </label>
      <input
        id={'owner-' + finding.id}
        type="text"
        className="mono cell-input"
        value={draft}
        disabled={!may}
        readOnly={write.busy}
        title={may ? undefined : refusalReason('finding.manage')}
        aria-describedby={dirty ? 'owner-dirty-' + finding.id : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          if (blurSaves(inDialog, e.relatedTarget, e.currentTarget)) commit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') setDraft(finding.ownerUserId)
        }}
        data-action="tprm.assessment,findings,risk,vendor,vendor-record.a01"
      />
      {dirty && (
        <p className="cell-sub cell-late" id={'owner-dirty-' + finding.id} role="status">
          {boxLine(write.busy, inDialog)}
        </p>
      )}
      <WriteOutcome state={write} />
    </div>
  )
}

export function DueCell({ finding, onChanged, save }: { finding: Finding; onChanged: (f: Finding) => void; save?: DialogSave }): JSX.Element {
  const inDialog = useInDialog()
  const [draft, setDraft] = useState(finding.dueOn)
  const write = useRowWrite(onChanged, save?.of('due'))
  const may = can('finding.manage')
  const dirty = draft !== finding.dueOn
  // D18 of the date rules: a finding is not moved into the past. Said, and
  // not sent, when the person leaves the cell.
  const [rule, setRule] = useState('')

  useEffect(() => setDraft(finding.dueOn), [finding.id, finding.dueOn])

  // On blur rather than on change. A date input reports a change the moment
  // the three parts make a whole date, so typing a year digit by digit writes
  // the year 0002 on the way to 2026.
  function commit() {
    if (write.busy) return
    if (!draft || draft === finding.dueOn) {
      setDraft(finding.dueOn)
      setRule('')
      return
    }
    const msg = checkFindingDue(draft, todayIso())
    setRule(msg)
    if (msg) return
    write.run(api.patchFinding(finding.id, { dueOn: draft }))
  }

  return (
    <div className="cell-edit">
      <label className="vh" htmlFor={'due-' + finding.id}>
        Due date for {finding.title}
      </label>
      <input
        id={'due-' + finding.id}
        type="date"
        className="cell-input"
        value={draft}
        disabled={!may}
        readOnly={write.busy}
        title={may ? undefined : refusalReason('finding.manage')}
        aria-describedby={
          [
            rule ? 'due-rule-' + finding.id : '',
            findingStanding(finding.status) ? 'due-help-' + finding.id : '',
            dirty ? 'due-dirty-' + finding.id : '',
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        aria-invalid={rule ? true : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          if (blurSaves(inDialog, e.relatedTarget, e.currentTarget)) commit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') {
            setDraft(finding.dueOn)
            setRule('')
          }
        }}
        data-action="tprm.assessment,findings,risk,vendor,vendor-record.a02"
      />
      {/* Only while it is owed. A closed or accepted finding's clock has
          stopped, and the status column already says which. */}
      {findingStanding(finding.status) && (
        <p className="cell-sub" id={'due-help-' + finding.id}>
          <Status tone={ageingTone(finding.ageingDays)}>{ageingText(finding.ageingDays)}</Status>
        </p>
      )}
      <DateRuleNote id={'due-rule-' + finding.id} message={rule} />
      {dirty && !rule && (
        <p className="cell-sub cell-late" id={'due-dirty-' + finding.id} role="status">
          {boxLine(write.busy, inDialog)}
        </p>
      )}
      <WriteOutcome state={write} />
    </div>
  )
}

// The hover on a severity chip: what set the severity before anybody changed it.
function severityOrigin(source: string): string {
  if (source === 'GATE') return 'From what the rule did to the band'
  if (source === 'ANSWER') return 'From the weight of the question'
  if (source === 'OBSERVED') return 'From what the website check found'
  return 'Set by whoever raised it'
}

export function SeverityCell({ finding, onChanged, save: dialog }: { finding: Finding; onChanged: (f: Finding) => void; save?: DialogSave }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [severity, setSeverity] = useState(finding.severity)
  const [reason, setReason] = useState('')
  const [missing, setMissing] = useState(false)
  const write = useRowWrite(onChanged, dialog?.of('severity'))
  const may = can('finding.manage')
  const overridden = finding.severity !== finding.derivedSeverity

  useEffect(() => setSeverity(finding.severity), [finding.id, finding.severity])

  function save() {
    if (severity === finding.severity) {
      setOpen(false)
      return
    }
    if (!reason.trim()) {
      setMissing(true)
      return
    }
    setMissing(false)
    write.run(api.patchFinding(finding.id, { severity, severityReason: reason.trim() }))
    setOpen(false)
  }

  return (
    <div className="cell-edit">
      {/* Where the severity came from is a hover on the chip rather than a
          line under it: it explains the system, and the row is for the work. */}
      <Status tone={severityTone(finding.severity)} title={severityOrigin(finding.source)}>
        {findingWords(finding.severity)}
      </Status>

      {/* An override is reported whatever raised the finding. The control is
          offered on every row, and a manual finding's derived value is what
          the person who raised it first wrote down, so hiding the pair there
          loses both the original reading and the reason it was replaced. */}
      {overridden && (
        <p className="cell-sub">
          {finding.source === 'MANUAL' ? 'Raised as ' : 'Derived '}
          <strong>{findingWords(finding.derivedSeverity)}</strong>
          {finding.severityReason ? ': ' + finding.severityReason : ''}
        </p>
      )}

      <button
        type="button"
        className="linkish"
        aria-expanded={open}
        aria-controls={'sev-form-' + finding.id}
        disabled={!may}
        title={may ? undefined : refusalReason('finding.manage')}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Leave it as it is' : 'Change the severity'}
      </button>

      {open && (
        <div className="cell-form" id={'sev-form-' + finding.id}>
          <label htmlFor={'sev-' + finding.id}>Treat as</label>
          <select
            id={'sev-' + finding.id}
            value={severity}
            aria-describedby={'sev-help-' + finding.id}
            onChange={(e) => setSeverity(e.target.value)}
          >
            {FINDING_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s +
                  (s === finding.derivedSeverity
                    ? finding.source === 'MANUAL'
                      ? ' (as it was raised)'
                      : ' (derived)'
                    : '')}
              </option>
            ))}
          </select>

          <label htmlFor={'sev-reason-' + finding.id}>Why</label>
          <input
            id={'sev-reason-' + finding.id}
            type="text"
            value={reason}
            aria-invalid={missing ? true : undefined}
            aria-describedby={'sev-help-' + finding.id + (missing ? ' sev-issue-' + finding.id : '')}
            onChange={(e) => {
              setReason(e.target.value)
              if (e.target.value.trim()) setMissing(false)
            }}
          />
          {missing && (
            <p className="field-issue" id={'sev-issue-' + finding.id}>
              Say why, so a reviewer can check it
            </p>
          )}

          <div className="chiprow">
            <button
              type="button"
              className="primary"
              disabled={write.busy}
              onClick={save}
              data-action="tprm.assessment,findings,risk,vendor,vendor-record.a03"
            >
              {write.busy ? 'Saving' : 'Save the severity'}
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <WriteOutcome state={write} />
    </div>
  )
}

export function StatusCell({ finding, onChanged, save }: { finding: Finding; onChanged: (f: Finding) => void; save?: DialogSave }): JSX.Element {
  const [pending, setPending] = useState<Move | null>(null)
  const [note, setNote] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [missing, setMissing] = useState(false)
  const [documents, setDocuments] = useState<VendorDocument[] | null>(null)
  const [documentsFailed, setDocumentsFailed] = useState(false)
  const write = useRowWrite(onChanged, save?.of('status'))
  const may = can('finding.manage')
  // On a page the moves sit in the cell. In a dialog they are the dialog's
  // actions and go in its footer, where they are in view however far down the
  // record this cell is.
  const inDialog = useInDialog()

  // The evidence of a fix is usually a document already filed against the
  // vendor, so the list is offered rather than an identifier asked for. Read
  // only once a resolution form is open, because most rows never open one.
  const resolving = pending?.to === 'RESOLVED'
  useEffect(() => {
    if (!resolving || documents !== null || documentsFailed) return
    let live = true
    api
      .vendorDocuments(finding.vendorId)
      .then((r) => {
        if (live) setDocuments(r.documents)
      })
      .catch(() => {
        if (live) setDocumentsFailed(true)
      })
    return () => {
      live = false
    }
  }, [resolving, documents, documentsFailed, finding.vendorId])

  // One box serves every move on the row, so opening a second form empties it.
  // Words typed for a withdrawal and then abandoned used to travel with the
  // next move made from this cell, and the API files them as that finding's
  // resolution note and puts them on the audit event.
  function openForm(move: Move) {
    if (pending?.to !== move.to) {
      setNote('')
      setDocumentId('')
      setMissing(false)
    }
    setPending(move)
  }

  function act(move: Move) {
    // Only the form that is open has anything to say about this move.
    const said = pending?.to === move.to ? note.trim() : ''
    if (move.requiresNote && !said) {
      openForm(move)
      setMissing(true)
      return
    }
    setMissing(false)
    // Resolution has a route of its own, which refuses a missing note as a
    // field rather than as a sentence about a transition, and takes the
    // evidence document. Everything else is a status on the patch.
    write.run(
      move.to === 'RESOLVED'
        ? api.resolveFinding(finding.id, { note: said, documentId: documentId || undefined })
        : api.patchFinding(finding.id, { status: move.to, note: said || undefined }),
      moveDone(finding.status, move.to),
    )
    setPending(null)
    setNote('')
    setDocumentId('')
  }

  const moves = movesFor(finding)
  const offered = moves.filter((m) => !m.byExceptionOnly)
  const byException = moves.filter((m) => m.byExceptionOnly)

  function closeForm() {
    setPending(null)
    setNote('')
    setDocumentId('')
    setMissing(false)
  }

  // The footer's button opens the reason box, which may be below the fold of
  // the record, so the keyboard is taken to it.
  const formFor = pending?.to
  useEffect(() => {
    if (inDialog && formFor) document.getElementById('note-' + finding.id)?.focus()
  }, [inDialog, formFor, finding.id])

  const press = (m: Move) => (m.requiresNote && pending?.to !== m.to ? openForm(m) : act(m))
  const { others, ...footer } = findingFooter(
    {
      moves: offered,
      pending,
      missing,
      refusal: may ? '' : refusalReason('finding.manage'),
      busy: write.busy,
      label: (to) => moveLabel(finding.status, to),
    },
    { press, confirm: act, back: closeForm },
  )

  return (
    <div className="cell-edit">
      <span className="sw-row">
        <Status tone={STATUS_WORD[finding.status]?.tone ?? 'neutral'}>
          {STATUS_WORD[finding.status]?.text ?? findingWords(finding.status)}
        </Status>
        {finding.status === 'ACCEPTED' && (
          <Toggletip label="When it opens again">
            Accepted through an approved exception. It opens again when the exception expires.
          </Toggletip>
        )}
        {finding.status === 'WITHDRAWN' && (
          <Toggletip label="Why nothing moves it">Final. If the same rule fires again, a new finding is raised.</Toggletip>
        )}
      </span>
      {/* The column carries the note of the last move that carried one, which
          is not always a closure: reopening writes here too, and that note is
          the thing a reader wants next to a finding that came back. The name
          is cleared when a finding returns to a standing status, so it appears
          only where it is the person who ended it. */}
      {finding.resolutionNote && (
        <p className="cell-sub">
          Last note: {finding.resolutionNote}
          {finding.resolvedBy ? ' (' + finding.resolvedBy + ')' : ''}
        </p>
      )}

      {inDialog && (pending || offered.length > 0) && (
        <DialogFooter
          {...footer}
          more={
            others.length > 0
              ? others.map((m) => (
                  <button
                    key={m.to}
                    type="button"
                    disabled={write.busy || !may}
                    title={may ? undefined : refusalReason('finding.manage')}
                    onClick={() => press(m)}
                    data-action="tprm.assessment,findings,risk,vendor,vendor-record.a05"
                  >
                    {moveLabel(finding.status, m.to)}
                  </button>
                ))
              : undefined
          }
        />
      )}

      {!inDialog && offered.length > 0 && (
        <div className="chiprow">
          {offered.map((m) => (
            <button
              key={m.to}
              type="button"
              className={m.to === 'RESOLVED' ? 'primary' : undefined}
              disabled={write.busy || !may}
              title={may ? undefined : refusalReason('finding.manage')}
              aria-expanded={m.requiresNote ? pending?.to === m.to : undefined}
              onClick={() => press(m)}
              data-action="tprm.assessment,findings,risk,vendor,vendor-record.a05"
            >
              {moveLabel(finding.status, m.to)}
            </button>
          ))}
        </div>
      )}

      {/* Acceptance is not a status anybody sets: it is what approving a risk
          exception against the finding does, so the row points there. */}
      {byException.length > 0 && (
        <p className="cell-sub">
          <a href="#/exceptions">Accept through an exception</a>
        </p>
      )}

      {pending && (
        <div className="cell-form">
          {/* One box, labelled Reason, whatever the move. The API files it as
              the finding's note and on the audit event for the move. */}
          <label htmlFor={'note-' + finding.id}>
            Reason{' '}
            <span aria-hidden="true" style={{ color: 'var(--crit)' }}>
              *
            </span>
          </label>
          <textarea
            id={'note-' + finding.id}
            rows={3}
            value={note}
            required={pending.requiresNote}
            aria-invalid={missing ? true : undefined}
            aria-describedby={missing ? 'note-issue-' + finding.id : undefined}
            onChange={(e) => {
              setNote(e.target.value)
              if (e.target.value.trim()) setMissing(false)
            }}
          />
          {missing && (
            <p className="field-issue" id={'note-issue-' + finding.id}>
              Enter a reason
            </p>
          )}

          {pending.to === 'RESOLVED' && (
            <>
              {/* Optional, and its state is said by the first option rather
                  than by a line of explanation under the box. */}
              <label htmlFor={'doc-' + finding.id}>Evidence</label>
              <Picker
                bare
                id={'doc-' + finding.id}
                label="Evidence"
                value={documentId}
                disabled={documents === null && !documentsFailed}
                options={(documents ?? []).map((d) => ({ value: d.id, label: d.title + ' (' + findingWords(d.docType) + ')' }))}
                none={
                  documentsFailed
                    ? 'Documents could not be read'
                    : documents === null
                      ? 'Reading the documents'
                      : documents.length === 0
                        ? 'No document on file'
                        : 'No document'
                }
                placeholder="Search the documents on file"
                noun="document"
                missing="A document not in this list"
                onChange={setDocumentId}
              />
            </>
          )}

          {!inDialog && (
            <div className="chiprow">
              <button
                type="button"
                className="primary"
                disabled={write.busy}
                onClick={() => act(pending)}
                data-action="tprm.assessment,findings,risk,vendor,vendor-record.a04"
                data-action-also="tprm.assessment,findings,risk,vendor,vendor-record.a05"
              >
                {write.busy ? 'Recording' : moveLabel(finding.status, pending.to)}
              </button>
              <button type="button" onClick={closeForm}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <WriteOutcome state={write} />
    </div>
  )
}

// What the finding is, on the row, and where it came from one press away. Who
// raised it and when is a record, not the work: it sits in the details beside
// what the finding says, and every raise is also on the audit trail as
// finding.raised.
function ProvenanceCell({ finding, showAssessment }: { finding: Finding; showAssessment: boolean }): JSX.Element {
  const stopped = !finding.stillReproducing && findingStanding(finding.status)
  return (
    <>
      <span className="cell-primary">{finding.title}</span>

      {finding.riskType && <span className="cell-sub">{finding.riskTypeName || findingWords(finding.riskType)}</span>}

      {finding.citation && (
        <span className="cites">
          <span className="cite">{finding.citation}</span>
        </span>
      )}

      {stopped && (
        <span className="cell-sub">
          <Status tone="info">Not reproducing</Status>{' '}
          <Toggletip label="What not reproducing means">
            The rule did not fire on the last score. That is not proof of a fix, so it stays open until closed with a
            reason.
          </Toggletip>
        </span>
      )}

      <details className="cell-detail">
        <summary className="muted">Details</summary>
        {finding.detail && <p>{finding.detail}</p>}
        <p className="cell-sub">
          {findingSourceWord(finding.source)}
          {finding.source === 'GATE' && finding.gateKey && (
            <>
              {' '}
              <span className="mono">{finding.gateKey}</span>
            </>
          )}
          {finding.raisedBy && (
            <>
              {', raised by '}
              <span className="mono">{finding.raisedBy}</span>
            </>
          )}
          {finding.raisedAt ? (finding.raisedBy ? ' on ' : ', ') + shortDate(finding.raisedAt) : ''}
        </p>
        {showAssessment && finding.assessmentId && (
          <p className="cell-sub">
            <a href={'#/assessment/' + encodeURIComponent(finding.assessmentId)}>The assessment it was last seen on</a>
          </p>
        )}
      </details>
    </>
  )
}

export function FindingsTable({
  caption,
  rows,
  showVendor,
  // False on the assessment's own panel, where every row came from the
  // assessment the reader is already looking at and the link points at the
  // page it is on.
  showAssessment = true,
  onChanged,
  // The save of the dialog the table is drawn in, which every write of its
  // cells is sent by (useRowWrite), each as an act of its own: the owner, the
  // due date, the severity, the status. Left out on a page.
  save,
  empty,
}: {
  caption: string
  rows: Finding[]
  showVendor: boolean
  showAssessment?: boolean
  onChanged: (f: Finding) => void
  save?: DialogSave
  empty: { title: string; detail: ReactNode }
}): JSX.Element {
  const columns: Column<Finding>[] = []

  if (showVendor) {
    columns.push({
      key: 'vendor',
      header: 'Vendor',
      sortValue: (f) => f.vendorName ?? f.vendorCode ?? f.vendorId,
      render: (f) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(f.vendorId)}>
            {f.vendorName || 'Open the vendor'}
          </a>
          {f.vendorCode && <span className="cell-sub mono">{f.vendorCode}</span>}
        </>
      ),
    })
  }

  columns.push(
    {
      key: 'finding',
      header: 'Finding',
      sortValue: (f) => f.title,
      render: (f) => <ProvenanceCell finding={f} showAssessment={showAssessment} />,
    },
    {
      key: 'severity',
      header: 'Severity',
      sortValue: (f) => SEVERITY_RANK[f.severity] ?? 0,
      render: (f) => <SeverityCell finding={f} onChanged={onChanged} save={save} />,
    },
    {
      key: 'owner',
      header: 'Owner',
      sortValue: (f) => f.ownerUserId,
      render: (f) => <OwnerCell finding={f} onChanged={onChanged} save={save} />,
    },
    {
      // Sorted on the ageing rather than the date, so descending is most
      // overdue first, which is the way this column is read.
      key: 'due',
      header: 'Due',
      sortValue: (f) => f.ageingDays,
      render: (f) => <DueCell finding={f} onChanged={onChanged} save={save} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (f) => STATUS_RANK[f.status] ?? 9,
      render: (f) => <StatusCell finding={f} onChanged={onChanged} save={save} />,
    },
  )

  // A reader without finding.manage sees every control greyed. The reason is
  // said once above the table rather than under every owner box, where it was
  // the same long sentence once per row.
  return (
    <>
      {!can('finding.manage') && rows.length > 0 && (
        <p className="hint sw-row sw-pad">
          <Status tone="neutral">Read only</Status>{' '}
          <ReasonTip label="Why these findings are read only" text={refusalReason('finding.manage')} />
        </p>
      )}
      <DataTable caption={caption} columns={columns} rows={rows} rowKey={(f) => f.id} empty={empty} />
    </>
  )
}

// The vendor record's own tab. No sort is imposed: the API returns the ones
// still owed first, most overdue first within that, and that is the order of
// the work.
export function VendorFindings({ vendorId }: { vendorId: string }): JSX.Element {
  const [rows, setRows] = useState<Finding[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    setRows(null)
    setError(null)
    api
      .vendorFindings(vendorId, { limit: 200 })
      .then((page) => {
        if (!live) return
        setRows(page.findings)
        setTotal(page.total)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId])

  const replace = useCallback((fresh: Finding) => {
    setRows((current) => (current ?? []).map((f) => (f.id === fresh.id ? mergedFinding(f, fresh) : f)))
  }, [])

  if (error != null) return <ErrorBox error={error} what="The findings on this vendor" />
  if (rows === null) return <Loading what="the findings on this vendor" />

  const owed = rows.filter((f) => findingStanding(f.status)).length
  const late = rows.filter((f) => findingStanding(f.status) && f.ageingDays > 0).length

  return (
    <section className="panel panel-flush" aria-labelledby="vendor-findings-heading">
      <div className="panel-head">
        <h2 id="vendor-findings-heading" style={{ margin: 0 }}>
          Findings
        </h2>
        {late > 0 && <Status tone="crit">{late} overdue</Status>}
      </div>

      <FindingsTable
        caption="Findings raised against this vendor, the ones still owed first."
        rows={rows}
        showVendor={false}
        onChanged={replace}
        empty={{
          title: 'Nothing raised against this vendor',
          detail: (
            <>
              A gate firing on a score raises one, or a person does{' '}
              <Toggletip label="What no findings means">
                A vendor with no scored assessment has no findings, which is not the same as a vendor with nothing
                wrong.
              </Toggletip>
            </>
          ),
        }}
      />

      {rows.length > 0 && (
        <ListCount shown={rows.length} total={total} noun="findings">
          , {owed} still owed
          {total > rows.length ? ', the read is capped and nothing is filtered' : ''}
        </ListCount>
      )}
    </section>
  )
}

// The findings this assessment's latest score raised or refreshed, shown where
// a reviewer asks why the band is what it is.
//
// The fold moves a finding's assessment pointer forward on every rescore, so a
// finding a later round refreshed belongs to that later round. This panel says
// what the score behind this assessment found, which is the honest reading and
// not the same as everything ever seen on the vendor.
export function AssessmentFindings({
  assessmentId,
  refreshKey,
}: {
  assessmentId: string
  refreshKey: number
}): JSX.Element {
  const [rows, setRows] = useState<Finding[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let live = true
    setRows(null)
    setError(null)
    api
      .assessmentFindings(assessmentId, { limit: 200 })
      .then((page) => {
        if (live) setRows(page.findings)
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [assessmentId, refreshKey])

  const replace = useCallback((fresh: Finding) => {
    setRows((current) => (current ?? []).map((f) => (f.id === fresh.id ? mergedFinding(f, fresh) : f)))
  }, [])

  const owed = (rows ?? []).filter((f) => findingStanding(f.status)).length

  return (
    <section className="panel panel-flush" aria-labelledby="assessment-findings-heading">
      <div className="panel-head">
        <h2 id="assessment-findings-heading" style={{ margin: 0 }}>
          What this score raised{' '}
          <Toggletip label="How a rescore treats findings">
            Rescoring moves which score a finding was last seen on, never its status. One that stops reproducing stays
            open, because a band moving is not proof of a fix.
          </Toggletip>
        </h2>
        <p className="summary-line" style={{ margin: 0 }}>
          {rows === null ? (
            <span className="muted">not read yet</span>
          ) : (
            <>
              <strong>{owed}</strong> still owed of <strong>{rows.length}</strong>
            </>
          )}
        </p>
      </div>

      {error != null ? (
        <div style={{ padding: '0 16px 12px' }}>
          <ErrorBox error={error} what="The findings from this assessment" />
        </div>
      ) : rows === null ? (
        <Loading what="the findings from this assessment" />
      ) : (
        <FindingsTable
          caption="Findings raised by the gates that fired when this assessment was scored."
          rows={rows}
          showVendor={false}
          showAssessment={false}
          onChanged={replace}
          empty={{
            title: 'No finding stands against this assessment',
            detail: 'No gate fired on the score, or it is not scored yet',
          }}
        />
      )}
    </section>
  )
}
